import { EditOutlined } from '@ant-design/icons';
import { Status, ThreeDotMenu } from '@mm/components';
import { useAdAudienceCRUDActions } from '@mm/state/actions/AdAudienceCRUDActions';
import { useFBOauthTokensCRUDActions } from '@mm/state/actions/FBOauthTokensCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useXRayConfigurationCRUDActions } from '@mm/state/actions/XRayConfiguration';
import AdAccountModel from '@mm/state/model/AdAccountModel';
import AdAudienceModel from '@mm/state/model/AdAudienceModel';
import {
  FACEBOOK_ADS,
  useConnectionsTableActions,
} from '@mm/state/modules/connectionsTableState/actions';
import { activeIntegrationSelector } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { fbOauthTokensShouldLoadSelector } from '@mm/state/selectors/fbOauthTokensSelectors';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import store from '@mm/state/store';
import { colors } from '@mm/utils';
import { isNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { GridCell } from '../../../../../../../../common/components/Grid/Grid';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import {
  ComponentDataItem,
  renderDataCell,
} from '../../../../../../contacts/components/ContactsTable/ContactTable';
import { FacebookAdsIntegration } from './FacebookAdsIntegration';
import { allOption } from './FacebookAdsIntegrationsHeader';
import { useAdAccount } from './use-ad-account';

import css from './FacebookAdsIntegrations.module.css';
import moment from 'moment';

export type IntegrationEntity = AdAudienceModel;
export type CellProps = { item: IntegrationEntity };

export const renderStatusCell = ({ item }: CellProps) => {
  const { synchronizeAutomatically } = item;
  return (
    <GridCell>
      <Status
        className={css.status}
        status={synchronizeAutomatically ? 'Automatically' : 'Manually'}
        indicatorSizePx={8}
        color={synchronizeAutomatically ? colors.green6 : colors.grayLight}
      />
    </GridCell>
  );
};

export const renderAudienceCreatedDate = ({ item }: CellProps) => (
  <GridCell>{moment(item.createdAt).format('MM/DD/YYYY HH:mm')}</GridCell>
);

export const renderAudienceEmailRowsSent = ({ item }: CellProps) => (
  <GridCell>{item.exportedEmailsCount || 0}</GridCell>
);

export const renderAudienceTotalRowsSent = ({ item }: CellProps) => (
  <GridCell>
    {' '}
    {(item.exportedVisitorsCount || 0) +
      (item.exportedEmailsCount || 0) +
      (item.exportedPageuidsCount || 0)}
  </GridCell>
);

export const renderAudienceVisitorRowsSent = ({ item }: CellProps) => (
  <GridCell>{item.exportedVisitorsCount || 0}</GridCell>
);

export const renderAudienceSizeCell = ({ item }: CellProps) => (
  <GridCell>
    {[item.approximateCountLowerBound, item.approximateCountUpperBound].every(
      isNumber,
    )
      ? `${item.approximateCountLowerBound} - ${item.approximateCountUpperBound}`
      : '-'}
  </GridCell>
);

export const renderAudienceCell = ({ item }: CellProps) => {
  const { audience } = item;
  const chatbotId = activeChatbotIdSelector(store.getState());
  if (audience?.name === 'All contacts') {
    return <GridCell>All website visitor data</GridCell>;
  }
  return (
    <GridCell>
      {audience ? (
        <Link
          className={css.link}
          key={audience?.id}
          to={BotBuilderRoutes.audiences.getLink({
            chatbotId,
            audienceId: audience?.id,
          })}
        >
          {audience?.name}
        </Link>
      ) : (
        <span>-</span>
      )}
    </GridCell>
  );
};

export const renderActionsCell = (
  item: IntegrationEntity,
  editSheet: (id: number) => void,
  deleteConnection: (item: IntegrationEntity) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return deleteConnection(item);
      default:
        break;
    }
  };

  const elements = [{ name: 'Delete', value: 'delete' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <GridCell className="action-cell">
      <EditOutlined
        onClick={() => editSheet(item.id)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </GridCell>
  );
};

export const renderAdAccountCell = (item: IntegrationEntity) => (
  <GridCell className="ad-account-cell">
    {item.adAccount
      ? `${item.adAccount.name} (${item.adAccount.remoteId})`
      : ''}
  </GridCell>
);

const generateTableCells = (
  editSheet: (id: number) => void,
  deleteConnection: (item: IntegrationEntity) => void,
  onChangeOrder: (orderBy: string) => void,
) => {
  const cells: ComponentDataItem[] = [
    {
      label: 'Facebook Ad Account',
      keyName: 'facebook_ad_account',
      onChange: onChangeOrder,
      Cell: ({ item }: CellProps) => renderAdAccountCell(item),
    },
    {
      label: 'Audience',
      keyName: 'audience',
      onChange: onChangeOrder,
      Cell: renderAudienceCell,
    },
    {
      label: 'Facebook Audience',
      keyName: 'facebook_audience',
      onChange: onChangeOrder,
      Cell: renderDataCell('name'),
    },
    {
      label: 'Audience Size',
      keyName: 'audience_size',
      onChange: onChangeOrder,
      Cell: renderAudienceSizeCell,
    },
    {
      label: 'Sync to Facebook',
      keyName: 'sync_automatically',
      onChange: onChangeOrder,
      Cell: renderStatusCell,
    },
    {
      label: 'Actions',
      keyName: 'actions',
      Cell: ({ item }: CellProps) =>
        renderActionsCell(item, editSheet, deleteConnection),
    },
  ];
  return cells;
};

export const FacebookAdsIntegrations = () => {
  const { setConnectionsOrderBy, setActiveIntegration } =
    useConnectionsTableActions();
  const activeIntegration = useSelector(activeIntegrationSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const adAudienceCRUDActions = useAdAudienceCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal } = useModalActions();

  const botId = useSelector(activeChatbotIdSelector);
  const xrayConfigurationCRUDACtions = useXRayConfigurationCRUDActions();
  const fbOauthTokensCRUDActions = useFBOauthTokensCRUDActions();
  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);
  const fbOauthTokensShouldLoad = useSelector(fbOauthTokensShouldLoadSelector);

  const adAccount = useAdAccount();

  const [selectedAdAccount, setSelectedAdAccount] = useState<AdAccountModel>(
    allOption as unknown as AdAccountModel,
  );

  const onEdit = (id: number) => {
    setActiveIntegrationHandler();
    showSlideoutMenu('fbAdsIntegrationEditor', {
      adAudienceId: id,
    });
  };
  const onDelete = (item: IntegrationEntity) => {
    const deleteCallback = () => {
      adAudienceCRUDActions.deleteAudienceByRemoteId(
        facebookPageId,
        item.remoteId,
      );
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${item.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };

  const onChangeOrder = orderBy => {
    setConnectionsOrderBy(orderBy);
  };

  const setActiveIntegrationHandler = () => {
    if (activeIntegration === FACEBOOK_ADS) return;
    setActiveIntegration(FACEBOOK_ADS);
  };

  const cells = generateTableCells(onEdit, onDelete, onChangeOrder);

  const fetchAdAccountsOptions = () => {
    adAccount.fetchRemoteAdAccounts();
    adAccount.fetchFacebookPageAdAccounts();
  };

  useEffect(() => {
    fetchAdAccountsOptions();
  }, []);

  useEffect(() => {
    // Load tokens only once if not fb page
    if (!isUserPageConnected && fbOauthTokensShouldLoad) {
      fbOauthTokensCRUDActions.retrieveAll({
        facebookPageId,
      });
    }
  }, [
    isUserPageConnected,
    fbOauthTokensShouldLoad,
    facebookPageId,
    fbOauthTokensCRUDActions,
  ]);

  useEffect(() => {
    adAudienceCRUDActions.retrieveAll(
      facebookPageId,
      selectedAdAccount?.id === 'all' ? undefined : selectedAdAccount?.id,
    );
  }, [selectedAdAccount?.id]);

  useEffect(() => {
    xrayConfigurationCRUDACtions.getXRayEventsConfiguration({ botId });
  }, [botId]);

  return (
    <FacebookAdsIntegration
      cellData={cells}
      name={FACEBOOK_ADS}
      adAccounts={adAccount.facebookPageAdAccounts}
      selectedAdAccount={selectedAdAccount}
      onPageConnectSuccess={fetchAdAccountsOptions}
      onSelectedAdAccountChange={opt => setSelectedAdAccount(opt)}
    />
  );
};
